/**
 * Trainer-side consent gate.
 *
 * Clients have been gated since 2026-08-10 (`clients.terms_accepted_at` +
 * the `accept_terms` RPC). Trainers had no consent record at all — signup
 * showed passive prose and stored nothing — which is the weaker half of the
 * record covering the people who actually pay for the product.
 *
 * Deliberately mirrors mobile's `useTermsAcceptance` so both platforms share
 * one rule and one version constant. Accepting on either platform satisfies
 * the other; neither re-prompts what the other just recorded.
 */

import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { logError } from "@/lib/error-utils";
import { TERMS_VERSION } from "@/lib/legal/version";

export { TERMS_VERSION };

/** The two consent columns on `profiles` that drive the gate. */
export interface TrainerConsentRow {
  terms_accepted_at: string | null;
  terms_version: string | null;
}

/**
 * Pure decision: must this trainer accept before continuing?
 *
 * Extracted so the rule is unit-testable without rendering.
 *
 * Requires BOTH a timestamp and a matching version. A version without a
 * timestamp is not a valid consent record, and a stale version means they
 * never saw the current disclosures — that is the whole point of storing the
 * version rather than a boolean.
 */
export function trainerNeedsAcceptance(row: TrainerConsentRow | null): boolean {
  if (!row) return true;
  return !row.terms_accepted_at || row.terms_version !== TERMS_VERSION;
}

/**
 * Tracks whether the signed-in trainer has accepted the current Terms.
 *
 * Fails OPEN on a network/query error, matching the client gate: someone who
 * cannot reach Supabase is not locked out of their own business. This gate is
 * a consent record, not a security boundary — RLS is what protects data — so
 * a transient failure must not brick the app. The next successful load
 * re-prompts.
 *
 * @param userId auth user id, or null/undefined when signed out or when the
 *               account is a client (clients use the `clients`-table gate).
 */
export function useTrainerTerms(userId: string | null | undefined) {
  const [needsAcceptance, setNeedsAcceptance] = useState(false);
  // Only start in a loading state when there is actually someone to check, so
  // signed-out visitors and client accounts are never held on a spinner
  // waiting for an effect that immediately no-ops.
  const [loading, setLoading] = useState(!!userId);

  const check = useCallback(async () => {
    if (!userId) {
      setNeedsAcceptance(false);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("terms_accepted_at, terms_version")
        .eq("id", userId)
        .maybeSingle();

      if (error) throw error;
      setNeedsAcceptance(trainerNeedsAcceptance(data ?? null));
    } catch (err) {
      logError(err, "useTrainerTerms.check");
      setNeedsAcceptance(false); // fail open — see docblock
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    check();
  }, [check]);

  /**
   * Record acceptance of the current version. Returns false on failure.
   *
   * Uses the `accept_trainer_terms` RPC rather than a direct UPDATE. Trainers
   * CAN update their own profiles row, so this is not about permission — it is
   * about integrity: a direct write would let the caller choose the version
   * string and the timestamp. Both are set server-side, because a consent
   * record is evidence and the party being bound does not get to author it.
   *
   * On success the local flag flips immediately, so the gate closes without a
   * round-trip. The value is persisted, so it stays closed on the next load,
   * on other devices, and after a reinstall.
   */
  const accept = useCallback(async (): Promise<boolean> => {
    if (!userId) return false;
    try {
      const { error } = await supabase.rpc("accept_trainer_terms", {
        p_terms_version: TERMS_VERSION,
      });

      if (error) throw error;
      setNeedsAcceptance(false);
      return true;
    } catch (err) {
      logError(err, "useTrainerTerms.accept");
      return false;
    }
  }, [userId]);

  return { needsAcceptance, loading, accept, refetch: check };
}
